/**
 * DocumentsPage - Pagina principale gestione documenti
 * 
 * Mostra l'elenco delle pratiche con cartella documenti configurata
 * e permette di navigare nei documenti di ciascuna pratica.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FolderOpen,
  Search,
  AlertCircle,
  Loader2,
  FileText,
  User,
  Calendar,
  ExternalLink,
  FolderX,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FileExplorer } from '@/components/documents';
import { isElectronEnvironment } from '@/hooks/useElectronFileSystem';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';
import { formatDate } from '@/utils/date';

interface PracticeWithDocuments {
  id: string;
  title: string;
  practiceNumber?: string;
  documentPath?: string | null;
  documentPathSetAt?: string | null;
  status: string;
  type: {
    id: string;
    name: string;
    code: string;
    color?: string;
  };
  client: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const [practices, setPractices] = useState<PracticeWithDocuments[]>([]);
  const [filteredPractices, setFilteredPractices] = useState<PracticeWithDocuments[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPractice, setSelectedPractice] = useState<PracticeWithDocuments | null>(null);

  // Carica tutte le pratiche
  useEffect(() => {
    const loadPractices = async () => {
      try {
        setLoading(true);
        // Carica tutte le pratiche (non archiviate)
        const response = await apiClient.get('/practices?limit=100');
        setPractices(response.data.data.practices);
        setFilteredPractices(response.data.data.practices);
      } catch (err: any) {
        setError(err.message || 'Errore nel caricamento delle pratiche');
      } finally {
        setLoading(false);
      }
    };
    loadPractices();
  }, []);

  // Filtra pratiche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPractices(practices);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = practices.filter(
      (p) =>
        p.title.toLowerCase().includes(query) ||
        p.practiceNumber?.toLowerCase().includes(query) ||
        p.client.firstName.toLowerCase().includes(query) ||
        p.client.lastName.toLowerCase().includes(query) ||
        p.type.name.toLowerCase().includes(query)
    );
    setFilteredPractices(filtered);
  }, [searchQuery, practices]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      IN_PROGRESS: 'bg-blue-100 text-blue-800',
      COMPLETED: 'bg-green-100 text-green-800',
      ARCHIVED: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const inElectron = isElectronEnvironment();
  const isSupported = inElectron || (typeof window !== 'undefined' && 'showDirectoryPicker' in window);

  const handlePathConfigured = async (practiceId: string, newPath: string) => {
    try {
      await apiClient.patch(`/practices/${practiceId}/document-path`, { documentPath: newPath });
      toast.success('Percorso documenti aggiornato');
      setPractices((prev) =>
        prev.map((p) => (p.id === practiceId ? { ...p, documentPath: newPath } : p))
      );
      if (selectedPractice?.id === practiceId) {
        setSelectedPractice((prev) => prev ? { ...prev, documentPath: newPath } : prev);
      }
    } catch {
      toast.error('Errore nel salvataggio del percorso documenti');
    }
  };

  if (!isSupported) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-center">
        <AlertCircle className="h-16 w-16 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Browser non supportato</h2>
        <p className="text-muted-foreground max-w-md">
          La gestione documenti richiede un browser che supporti la File System Access API.
          Per favore usa <strong>Google Chrome</strong>, <strong>Microsoft Edge</strong> o{' '}
          <strong>Opera</strong>.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-center">
        <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Errore</h2>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Documenti</h1>
          <p className="text-muted-foreground">
            Gestione documenti delle pratiche
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca pratica, cliente, tipo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredPractices.length} pratiche 
          ({filteredPractices.filter(p => p.documentPath).length} con documenti configurati)
        </div>
      </div>

      {/* Practices Grid */}
      {filteredPractices.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderX className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Nessuna pratica trovata</h3>
            <p className="text-muted-foreground text-center max-w-md">
              {practices.length === 0
                ? 'Nessuna pratica presente nel sistema.'
                : 'Nessuna pratica corrisponde ai criteri di ricerca.'}
            </p>
            {practices.length === 0 && (
              <Button
                className="mt-4"
                onClick={() => navigate('/practices')}
              >
                Vai alle Pratiche
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPractices.map((practice) => {
            const hasDocuments = !!practice.documentPath;
            return (
              <Card
                key={practice.id}
                className={`hover:shadow-md transition-shadow cursor-pointer ${!hasDocuments ? 'opacity-75' : ''}`}
                onClick={() => hasDocuments ? setSelectedPractice(practice) : navigate(`/practices/${practice.id}?tab=documents`)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <FolderOpen className={`h-5 w-5 ${hasDocuments ? 'text-amber-500' : 'text-gray-400'}`} />
                      <CardTitle className="text-base">
                        {practice.practiceNumber && (
                          <span className="text-muted-foreground mr-2">#{practice.practiceNumber}</span>
                        )}
                        {practice.title}
                      </CardTitle>
                    </div>
                    <Badge className={getStatusColor(practice.status)}>{practice.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4" />
                      {practice.client.firstName} {practice.client.lastName}
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <FileText className="h-4 w-4" />
                      {practice.type.name}
                    </div>
                    {hasDocuments ? (
                      <div className="flex items-center gap-2 text-green-600">
                        <FolderOpen className="h-4 w-4" />
                        Documenti: {practice.documentPath}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-orange-500">
                        <AlertCircle className="h-4 w-4" />
                        Documenti non configurati
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex justify-between items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/practices/${practice.id}`);
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Dettagli
                    </Button>
                    {hasDocuments ? (
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); setSelectedPractice(practice); }}>
                        <FolderOpen className="h-4 w-4 mr-1" />
                        Apri
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          navigate(`/practices/${practice.id}?tab=documents`); 
                        }}
                      >
                        <FolderOpen className="h-4 w-4 mr-1" />
                        Configura
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* File Explorer Dialog */}
      <Dialog
        open={!!selectedPractice}
        onOpenChange={(open) => !open && setSelectedPractice(null)}
      >
        <DialogContent className="max-w-5xl h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-amber-500" />
              {selectedPractice?.practiceNumber && (
                <span className="text-muted-foreground">#{selectedPractice.practiceNumber}</span>
              )}
              {selectedPractice?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedPractice && (
            <FileExplorer
              practiceId={selectedPractice.id}
              basePath={selectedPractice.documentPath || undefined}
              onPathConfigured={(path) => handlePathConfigured(selectedPractice.id, path)}
              className="h-[calc(80vh-80px)]"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
